import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/auth';
import type { LoginResponse } from '@/types/api';

interface UseAuthReturn {
  isAuthenticated: boolean;
  user: { email?: string; username: string; is_admin?: boolean } | null;
  login: (username: string, password: string) => Promise<void>;
  loginWithRedirect: () => void;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const navigate = useNavigate();
  const {
    accessToken,
    user,
    isAuthenticated,
    setAuth,
    setUser,
    logout: logoutStore,
  } = useAuthStore();

  const login = useCallback(
    async (username: string, password: string): Promise<void> => {
      const response: LoginResponse = await authService.login(username, password);
      setAuth(response, {
        username,
        email: username,
      });
    },
    [setAuth]
  );

  const loginWithRedirect = useCallback((): void => {
    authService.loginWithRedirect();
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } finally {
      // Always clear local state so the user is logged out even if the backend call fails (e.g. 401, network)
      logoutStore();
      navigate('/login', { replace: true });
    }
  }, [logoutStore, navigate]);

  const getCurrentUser = useCallback(async (): Promise<void> => {
    const u = await authService.getCurrentUser();
    setUser(u);
  }, [setUser]);

  return {
    isAuthenticated: isAuthenticated || !!accessToken || !!user,
    user,
    login,
    loginWithRedirect,
    logout,
    getCurrentUser,
  };
}
